// On site. One question, answered in under two seconds.
//
// Large type, high contrast, no scroll for the primary answer, no polling and no
// animation. This screen is read with a cracked phone in daylight in a dust storm
// on the third day of a dead battery, and it answers where to be and how long the
// walk is. Nothing else.

import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useApp } from '../../data/store.tsx';
import { fmtRange, fmtTime, minutesBetween } from '../../engine/time.ts';
import { walkMinutes } from '../../engine/walk.ts';
import { stageColor } from '../theme.ts';
import '../../styles/event-refinements.css';

export function EventMode() {
  const { state, derived } = useApp();
  // One tick a minute, and only while this screen is mounted. No polling, no sync.
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const timer = window.setInterval(() => setNow(Date.now()), 60_000);
    return () => window.clearInterval(timer);
  }, []);

  // What they picked, in time order. Event mode reports their plan back to
  // them; it does not compute one.
  const blocks = [...derived.picksByDay.values()]
    .flat()
    .map((p) => ({
      setId: p.setId,
      stageId: p.set.stageId,
      startTs: p.startTs,
      endTs: p.endTs,
      mode: 'full' as const,
      locked: false,
      utility: 0,
    }))
    .sort((a, b) => a.startTs - b.startTs);

  const current = blocks.find((b) => b.startTs <= now && b.endTs > now);
  const next = blocks.find((b) => b.startTs > now);

  const describe = (setId?: string) => {
    if (!setId) return { act: 'Rest at camp', stage: 'Camp' };
    const set = derived.setById.get(setId);
    const artist = set ? derived.artistById.get(set.artistId) : undefined;
    const stage = set ? derived.stageById.get(set.stageId) : undefined;
    return { act: artist?.name ?? 'Unknown', stage: stage?.name ?? '' };
  };

  const walkNext =
    current && next && state.walk
      ? walkMinutes(state.walk, current.stageId, next.stageId)
      : 0;

  const nowInfo = current ? describe(current.setId) : null;
  const nextInfo = next ? describe(next.setId) : null;

  // Every stage, whatever is on it this minute, whether or not any of it was
  // picked. Somebody who planned nothing still came to the same field and still
  // asks the same question.
  const playing = state.stages
    .filter((stage) => !stage.isNonMusic)
    .map((stage) => {
      const onStage = state.sets
        .filter((set) => set.stageId === stage.id)
        .sort((a, b) => a.startTs - b.startTs);
      const live = onStage.find((set) => set.startTs <= now && set.endTs > now);
      const upcoming = onStage.find((set) => set.startTs > now);
      const nameOf = (setId?: string) =>
        setId ? derived.artistById.get(derived.setById.get(setId)?.artistId ?? '')?.name : undefined;
      return {
        stage,
        live,
        upcoming,
        liveName: nameOf(live?.id),
        upcomingName: nameOf(upcoming?.id),
        picked: live ? derived.pickedSetIds.has(live.id) : false,
      };
    })
    .filter((row) => row.live || row.upcoming);

  return (
    <div className="event-page">
      <div className="event">
        <header className="event-head"><h1>At the stages</h1><p>{fmtTime(now)} Eastern · published set times</p><nav aria-label="Event navigation"><Link to="/home">Home</Link><Link to="/plan">My plan</Link><Link to="/news">Updates</Link><Link to="/weather">Weather &amp; radar</Link></nav></header>
        <div className="panel">
          <p>These times follow the saved lineup. Check <Link to="/news">current updates</Link> for delays, closures and departure information.</p>
        </div>
        <div className="now">
          <div className="label">Scheduled now</div>
          {nowInfo ? (
            <>
              <div className="act">{nowInfo.act}</div>
              <div className="where">{nowInfo.stage}</div>
              <div className="when">
                {fmtRange(current!.startTs, current!.endTs)} · {minutesBetween(now, current!.endTs)} min left
              </div>
            </>
          ) : (
            <>
              <div className="act">{blocks.length ? 'No saved set at this time' : 'Your plan is empty'}</div>
              {playing.length > 0 && <div className="when">See the stage schedules below.</div>}
              <Link className="event-plan-link" to="/plan">{blocks.length ? 'View your plan' : 'Browse sets and make a plan'}</Link>
            </>
          )}
        </div>

        {nextInfo && next && (
          <div className="now">
            <div className="label">Next</div>
            <div className="act" style={{ fontSize: 26 }}>
              {nextInfo.act}
            </div>
            <div className="where">{nextInfo.stage}</div>
            <div className="when">
              {fmtTime(next.startTs)}
              {walkNext > 0 ? ` · ${walkNext} min walk, leave by ${fmtTime(next.startTs - walkNext * 60000)}` : ''}
            </div>
          </div>
        )}

        <div className="panel">
          <h2>Scheduled at each stage</h2>
          {playing.length === 0 ? (
            <p className="muted" style={{ fontSize: 16, margin: 0 }}>
              No current or upcoming sets in the saved lineup. Check official updates for changes.
            </p>
          ) : (
            <div className="rowlist">
              {playing.map((row) => (
                <div key={row.stage.id} className="row" style={{ borderLeft: `4px solid ${stageColor(row.stage.id)}` }}>
                  <span>
                    {row.live ? (
                      <b>{row.liveName}</b>
                    ) : (
                      <span className="muted">Between sets</span>
                    )}
                    <span className="sub" style={{ display: 'block' }}>
                      {row.stage.name}
                      {row.live ? ` · ${minutesBetween(now, row.live.endTs)} min left` : ''}
                      {row.upcomingName
                        ? ` · then ${row.upcomingName} at ${fmtTime(row.upcoming!.startTs)}`
                        : ''}
                    </span>
                  </span>
                  {row.picked && <span className="badge good">yours</span>}
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="panel">
          <h2>Your group</h2>
          {state.members.length === 0 && <p>Create or join a group to compare plans.</p>}
          <div className="rowlist">
            {state.members.map((m) => (
              <div key={m.userId} className="row">
                <span>
                  <span
                    style={{
                      display: 'inline-block',
                      width: 9,
                      height: 9,
                      borderRadius: 999,
                      background: m.color,
                      marginRight: 7,
                    }}
                  />
                  {m.displayName}
                </span>
                <span className="muted" style={{ fontSize: 14 }}>
                  {m.userId === state.userId ? nowInfo ? `${nowInfo.stage} in your plan` : 'No set planned now' : 'See group plan'}
                </span>
              </div>
            ))}
          </div>
          <Link className="event-plan-link" to={state.members.length ? '/group' : '/search'}>{state.members.length ? 'Open group planning' : 'Find people and groups'}</Link>
        </div>

        <div className="event-foot">
          <Link to="/plan">Back to My plan</Link>
          <span className="muted">
            {state.cachedAt ? `Saved locally ${new Date(state.cachedAt).toLocaleString('en-US', { timeZone: 'America/New_York', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit', timeZoneName: 'short' })}` : 'No local cache yet'}
          </span>
        </div>
      </div>
    </div>
  );
}
